"use client";

import { Activity, ArrowRight, Baby, HeartPulse, Microscope, ShieldCheck, Stethoscope } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  icon: ShieldCheck,
  title: "Property Administration",
  description: "Rigorous financial oversight and administrative management to ensure your real estate assets operate with institutional precision."
}, {
  icon: Activity,
  title: "Rental Optimization",
  description: "Data-driven yield strategies and dynamic pricing models designed to maximize occupancy and revenue for fractional owners."
}, {
  icon: Microscope,
  title: "Legal Compliance",
  description: "Full PROFECO and SAT regulatory alignment, ensuring every transaction and contract meets Mexican federal standards."
}, {
  icon: HeartPulse,
  title: "Maintenance Coordination",
  description: "Proactive facility management and rapid-response repairs to preserve the architectural integrity of your luxury investment."
}, {
  icon: Baby,
  title: "Owner Reporting",
  description: "Transparent, ledger-style financial statements and performance metrics delivered with absolute clarity and punctuality."
}, {
  icon: Stethoscope,
  title: "Leasing & Logistics",
  description: "End-to-end tenant screening and lease management, bridging the gap between high-end hospitality and secure ownership."
}];
function ServiceCard({
  service,
  index
}: {
  service: typeof services[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const Icon = service.icon;
  return <div ref={ref} id="management" data-index={index} style={{
    transitionDelay: `${index * 80}ms`
  }} className={`group relative flex flex-col rounded-md border border-platinum-border bg-card p-8 sm:p-10 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] hover:border-primary ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      <div className="mb-8">
        <div className="flex h-[88px] w-[88px] items-center justify-center rounded-md bg-secondary/5 transition-colors duration-500 group-hover:bg-primary">
          <Icon className="h-[44px] w-[44px] text-primary transition-colors duration-500 group-hover:text-secondary" strokeWidth={1.25} />
        </div>
      </div>

      <h3 className="font-default text-2xl font-semibold text-primary mb-3">
        {service.title}
      </h3>

      <p className="font-sans text-[15px] leading-relaxed text-slate-600 mb-8 flex-1">
        {service.description}
      </p>

      <Link to="/ManagementPage" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-secondary hover:text-primary transition-colors duration-300">
        <span>Learn more</span>
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </div>;
}
export default function Management() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="668" id="management" className="bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={introRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16 md:mb-20 lg:mb-24 transition-all duration-700 ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-6 flex flex-col">
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-primary mb-6">
              02 / Management
            </span>
            <h2 className="font-default text-4xl sm:text-5xl lg:text-[56px] leading-[1.1] font-semibold text-slate-900 tracking-tight">
              <span className="inline-block w-1 h-12 bg-primary mr-4 align-middle" />
              Precision in Asset Stewardship.
            </h2>
            <p className="font-sans text-base md:text-lg leading-relaxed text-slate-600 mt-8 max-w-xl">
              From administrative rigor to maintenance excellence, IMI Elite Management provides the institutional framework required to protect and grow your Cabo real estate portfolio.
            </p>
          </div>

          <div className="lg:col-span-6 lg:pt-24">
            <p className="font-sans text-base md:text-lg leading-relaxed text-slate-600 max-w-xl">We believe management is an architectural discipline. Our team integrates 29 years of resort expertise with strict legal compliance to ensure your property isn't just maintained, but optimized for maximum market performance and owner peace of mind.</p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, i) => <ServiceCard key={service.title} service={service} index={i} />)}
        </div>

        {}
        <div className="mt-16 md:mt-20 flex justify-center">
          <Link to="/ManagementPage" className="group inline-flex items-center gap-2 font-mono text-sm tracking-widest uppercase text-primary hover:text-secondary transition-colors duration-300">
            <span className="border-b border-primary/30 group-hover:border-secondary/50 pb-1 transition-colors duration-300">
              Explore Management Solutions
            </span>
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>;
}